import {type QueryClient, useQueryClient} from '@github-ui/react-query'
import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useMemo, useState} from 'react'

import {
  fetchTimelineItems,
  type TimelineFeed,
  type TimelineIssue,
  type TimelineItemsPageArgs,
  type TimelineItemsPageParams,
  timelineItemsQueryKey,
  type TimelineQueryOptions,
  timelineQueryOptions,
} from './fetch-timeline-items'

export function useTimelineFocusQueryOptions({
  owner,
  repo,
  number,
  feed = 'activity',
  params,
  recoverMissingFocus = true,
}: {
  owner: string
  repo: string
  number: number
  feed?: TimelineFeed
  params: TimelineItemsPageParams | undefined
  recoverMissingFocus?: boolean
}): TimelineQueryOptions | undefined {
  const queryClient = useQueryClient()
  const requestedFocus = params?.focusText
  const scope = `${owner}/${repo}/${number}/${requestedFocus ?? ''}`
  // Only the query anchor moves; the URL highlight and scroll target stay unchanged.
  const [anchor, setAnchor] = useState({scope, focusText: requestedFocus})
  if (anchor.scope !== scope) setAnchor({scope, focusText: requestedFocus})

  const onAnchorChange = useCallback(
    (from: string, to: string) =>
      setAnchor(current =>
        current.scope === scope && current.focusText === from ? {...current, focusText: to} : current,
      ),
    [scope],
  )

  return useMemo(() => {
    if (!params) return undefined
    const anchoredParams = {...params, focusText: anchor.scope === scope ? anchor.focusText : params.focusText}
    return {
      ...timelineQueryOptions(owner, repo, number, anchoredParams, feed),
      queryFn: ({signal}: {signal?: AbortSignal}) =>
        fetchTimelineFocus(
          queryClient,
          {owner, repo, number, feed, ...anchoredParams},
          signal,
          onAnchorChange,
          recoverMissingFocus,
        ),
    }
  }, [owner, repo, number, feed, params, anchor, scope, queryClient, onAnchorChange, recoverMissingFocus])
}

async function fetchTimelineFocus(
  queryClient: QueryClient,
  {owner, repo, number, feed, ...params}: TimelineItemsPageArgs,
  signal: AbortSignal | undefined,
  onFocusChange: (from: string, to: string) => void,
  recoverMissingFocus: boolean,
): Promise<TimelineIssue> {
  const key = timelineItemsQueryKey(owner, repo, number, params, feed)
  const previous = queryClient.getQueryData<TimelineIssue>(key)
  // This is the normal query read; only an empty result needs a replacement fetch.
  const issue = await fetchTimelineItems({owner, repo, number, feed, ...params}, signal)
  const focusText = params.focusText
  if (
    !recoverMissingFocus ||
    !focusText ||
    issue.timelineItems?.edges?.length ||
    !previous ||
    previous.id !== issue.id ||
    signal?.aborted
  ) {
    return issue
  }

  const highlight = getHighlightedEvent(focusText)
  const nodes = (previous.timelineItems?.edges ?? []).flatMap(edge =>
    edge?.node && (!highlight || !isEventHighlighted(edge.node, highlight)) ? [edge.node] : [],
  )
  const middle = Math.floor(nodes.length / 2)
  const candidates = nodes
    .map((node, index) => ({node, distance: Math.abs(index - middle)}))
    .sort((left, right) => left.distance - right.distance)

  for (const {node} of candidates) {
    // Cross references have their own IDs, which the endpoint cannot resolve as issue events.
    if (!('databaseId' in node) || node.databaseId == null || node.__typename === 'CrossReferencedEvent') continue
    const nextFocus = `${node.__typename === 'IssueComment' ? 'issuecomment' : 'event'}-${node.databaseId}`
    const nextParams = {...params, focusText: nextFocus}
    const replacement = await fetchTimelineItems({owner, repo, number, feed, ...nextParams}, signal)
    if (signal?.aborted) return issue
    if (!replacement.timelineItems?.edges?.length) continue

    // Warm the new key before switching so the loaded window never drops out.
    queryClient.setQueryData(timelineItemsQueryKey(owner, repo, number, nextParams, feed), replacement)
    onFocusChange(focusText, nextFocus)
    return replacement
  }

  return issue
}
